import {
  RecapResponse,
  Page,
  Form,
  Field,
  Formula,
  FieldType,
  FormulaOperand,
  FullRecapTaskData,
  Validation,
  FieldResponse,
  FormResponse,
} from '../../types/recap'
import { onScopeDispose, ref, computed, watch, watchEffect, nextTick, unref } from 'vue'
import RecapPage from '../components/recaps/RecapPage.vue'
import RecapForm from '../components/recaps/RecapForm.vue'
import RecapField from '../components/recaps/RecapField.vue'
import { StoreRecapStore, useStoreRecaps, StoreRecapStoreRefs } from '../../store/useStoreRecaps'
import { RecapStore, RecapStoreRefs, useRecapStore } from '../../store/useRecapStore'
import { storeToRefs } from 'pinia'
import { createSharedComposable } from '@vueuse/core'
import { Nullish } from '../../types/utility'
import { useUserStore } from '../../store/useUserStore'
import { LanguageChoice } from '../../i18n'
import { useStore } from '../../store'
import { useI18n } from 'vue-i18n'
import { useRecapHelpers } from './useRecapHelpers'
import { RecapResponsesModel } from '../../models/RecapReponsesModel'
import { RecapImagesModel } from '../../models/RecapImagesModel'
import { isNullishString, logInfo, parseBool } from '../../utils'
import { Notify, scroll } from 'quasar'
import { addBreadcrumb } from '@sentry/vue'
import { StoreRecapResponsesModel } from '../../models/StoreRecapResponsesModel'

export const recapTaskData = ref<FullRecapTaskData>({})

export const useSharedRecap = createSharedComposable(useRecap)

function useRecap() {
  // Data
  const recapId = ref('')
  const recapType = ref<'store' | 'task'>('task')
  const taskOrStoreId = ref('')
  const canSubmitRecap = ref(false)
  const pages = ref<(typeof RecapPage)[]>([])
  const forms = ref<(typeof RecapForm)[]>([])
  const fields = ref<(typeof RecapField)[]>([])
  const fieldInstanceList = computed<{ repeating_index: number; instance_id: string }[]>(() =>
    fields.value.map((field) => ({
      repeating_index: (field as any).getRepeatingIndex(),
      instance_id: (field as any).getFieldId(),
    }))
  )
  const formLogicDependencies = ref<{ [key: string]: string[] }>({})
  const fieldLogicDependencies = ref<{ [key: string]: string[] }>({})
  const storeRecapKey = computed(() => `${taskOrStoreId.value}-${recapId.value}`)
  const recapStore = ref<RecapStore | StoreRecapStore | null>(null)
  const recapStoreToRefs = ref<RecapStoreRefs | StoreRecapStoreRefs | null>(null)
  const recap = computed(() => recapStoreToRefs.value?.recaps[storeRecapKey.value])
  const recapDefinition = computed(() => recap.value?.recapDefinition)
  const errorSubmittingRecap = ref(false)

  // Helpers
  const { isFieldBlock } = useRecapHelpers()

  // Setup Function
  async function setUpComposable(
    recap_id: string,
    recap_type: 'store' | 'task',
    task_or_store_id: string,
    testingRecap?: boolean,
    testingTaskId?: string,
    previewRecap?: boolean
  ) {
    recapId.value = recap_id
    recapType.value = recap_type
    taskOrStoreId.value = task_or_store_id

    // Pick which recap store to use
    if (recapType.value === 'store') {
      recapStore.value = useStoreRecaps()
    } else {
      recapStore.value = useRecapStore()
    }
    recapStoreToRefs.value = storeToRefs(recapStore.value as any) as any

    // Get the recap definition
    if (testingRecap && !previewRecap) {
      // Fetch the recap, any recap, if we're testing recaps
      await recapStore.value
        .fetchRecap(recapId.value, recapType.value === 'task' ? testingTaskId ?? '0' : '0')
        .catch(() => null)
    } else {
      if (previewRecap) {
        taskOrStoreId.value = '0'
      }
      if (!recapStoreToRefs.value!.recaps[storeRecapKey.value]) {
        // We're executing a real recap, so it should have synced. Find it and add it to the Pinia store
        if (recapType.value === 'store') {
          await useStoreRecaps().setRecapFromDatabase(recap_id, task_or_store_id)
        } else {
          await useRecapStore().setRecapFromDatabase(recap_id, task_or_store_id)
        }
      }
    }
    await nextTick()

    // Populate Dependency Maps
    for (const page of recapDefinition.value?.pages || []) {
      for (const form of page.items) {
        for (const fieldOrFieldBlock of form.items) {
          if (isFieldBlock(fieldOrFieldBlock)) {
            for (const field of fieldOrFieldBlock.items) {
              fieldLogicDependencies.value[field.id] = getLogicDependenciesFromField(field)
            }
          } else {
            fieldLogicDependencies.value[fieldOrFieldBlock.id] =
              getLogicDependenciesFromField(fieldOrFieldBlock)
          }
        }
        formLogicDependencies.value[form.id] = getLogicDependenciesFromForm(form)
      }
    }
    await nextTick()
    await selectPage(0)
  }

  // Hierarchy Items
  const pageToFormMappings = ref<{ [key: string]: Form[] }>({})
  const formToFieldMappings = ref<{ [key: string]: Field[] }>({})
  watch(recapDefinition, () => {
    if (recapDefinition.value) {
      for (const page of recapDefinition.value.pages) {
        for (const form of page.items) {
          if (!Array.isArray(pageToFormMappings.value[page.id])) {
            pageToFormMappings.value[page.id] = []
          }
          pageToFormMappings.value[page.id].push(form)
          for (const fieldOrFieldBlock of form.items) {
            if (isFieldBlock(fieldOrFieldBlock)) {
              for (const field of fieldOrFieldBlock.items) {
                if (!Array.isArray(formToFieldMappings.value[form.id])) {
                  formToFieldMappings.value[form.id] = []
                }
                formToFieldMappings.value[form.id].push(field)
              }
            } else {
              if (!Array.isArray(formToFieldMappings.value[form.id])) {
                formToFieldMappings.value[form.id] = []
              }
              formToFieldMappings.value[form.id].push(fieldOrFieldBlock)
            }
          }
        }
      }
    } else {
      pageToFormMappings.value = {}
      formToFieldMappings.value = {}
    }
  })

  // Recap Submission Data
  const submittingPage = ref(false)
  const submittingRecap = ref(false)

  // Conditional Logic
  const triggerAllConditions = ref(false)
  function getAreAllFormsForPageHidden(pageId: string) {
    // Fetch all the field refs for the form
    const formIds = pageToFormMappings.value[pageId]
    if (formIds && formIds.length) {
      const formRefs = forms.value.filter((ref) => formIds.includes((ref as any).getFormId()))
      return formRefs.every((el) => (el as any).getIsFormHidden())
    }
    return false
  }
  function getAreAllFieldsForFormHidden(formId: string) {
    // Fetch all the field refs for the form
    const fieldIds = formToFieldMappings.value[formId]
    if (fieldIds && fieldIds.length) {
      const fieldRefs = fields.value.filter((ref) => fieldIds.includes((ref as any).getFieldId()))
      if (fieldRefs.length) {
        return fieldRefs.every((el) => (el as any).shouldBeHidden || !(el as any).getIsVisible())
      }
    }
    return false
  }

  // Language Support
  const { t } = useI18n({ useScope: 'global' })
  const { language } = storeToRefs(useUserStore())
  const recapLanguage = ref<LanguageChoice>(language.value)

  // Page Logic
  const allPages = computed(() => {
    return (
      recapDefinition.value?.pages.map((page, index) => {
        return {
          label: `${t('recap.page.page')} ${index + 1}`,
          value: page.id,
        }
      }) || []
    )
  })
  const hiddenPages = ref<string[]>([])
  watchEffect(() => {
    const hiddenPagesArray: string[] = []
    allPages.value.forEach((page) => {
      if (getAreAllFormsForPageHidden(page.value)) {
        hiddenPagesArray.push(page.value)
      }
    })
    if (!hiddenPages.value.every((pageId) => hiddenPagesArray.includes(pageId))) {
      hiddenPages.value = hiddenPagesArray
    }
  })
  const pageOptions = computed(() =>
    allPages.value.filter((option: PageOption) => !hiddenPages.value.includes(option.value))
  )
  const selectedPage = ref<PageOption>(pageOptions.value[0])
  async function selectPage(index: number) {
    submittingPage.value = true
    await nextTick()
    selectedPage.value = pageOptions.value[index]
    await nextTick()
  }

  // Value and Data Submission Functions
  async function getRecapResponse(
    applyValidation: boolean = true
  ): Promise<RecapResponse | { validationError: boolean } | null> {
    errorSubmittingRecap.value = false
    const errorsArray: number[] = []
    let filteredFormRefs: any[] = forms.value.filter((ref) => !!ref)

    // Run validation logic if applicable
    if (applyValidation) {
      filteredFormRefs = filteredFormRefs.map((form) => {
        // If the form is hidden, skip the validation for its fields
        if (form.getFormIsHidden()) {
          return
        }
        const fieldIdsInForm = formToFieldMappings.value[(form as any).getFormId()].map((r) => r.id)
        fields.value
          .filter(
            (ref) =>
              fieldIdsInForm.includes((ref as any).getFieldId()) &&
              (ref as any).getRepeatingIndex() === (form as any).getRepeatingIndex()
          )
          .forEach((ref) => {
            const dependenceFieldIds: string[] =
              fieldLogicDependencies.value[(ref as any).getFieldId()]
            // Get the refs for all the dependence fields
            const dependenceRefs: (typeof RecapField)[] = fields.value.filter((field) =>
              dependenceFieldIds.includes((field as any).getFieldId())
            )
            // Run validation against each field
            errorsArray.push((ref as any).runValidation(ref, dependenceRefs))
          })
        return form
      })
      canSubmitRecap.value = errorsArray.length === 0 || !errorsArray.some((num) => num !== 0)
    } else {
      // Ignore validation and save the response
      canSubmitRecap.value = true
    }

    // Gather recap responses
    if (canSubmitRecap.value) {
      canSubmitRecap.value = false

      // This is here to solve an edge case where, somehow, multiples of a form are being saved.
      // We only want the newest forms, so filter out the second instance of a form
      // This is accomplished by reversing the array before we traverse it looking for duplicates
      filteredFormRefs = filteredFormRefs
        .reverse()
        .filter((ref, index) => {
          if ((ref as any).getFormType() === 'simple') {
            return (
              index ===
              filteredFormRefs.findIndex(
                (formRef) => (formRef as any).getFormId() === (ref as any).getFormId()
              )
            )
          }
          // Don't filter out duplicate survey or product forms
          // These form types can repeat, so we don't want to mistakenly delete a perfectly valid one!
          return true
        })
        // Reverse again so the forms are all in the proper order
        .reverse()

      // Gather recap responses
      const form_responses: FormResponse[] = []
      for (const form of filteredFormRefs) {
        const fieldIdsInForm = formToFieldMappings.value[(form as any).getFormId()].map((r) => r.id)
        const allFieldRefs = fields.value.filter(
          (field) =>
            fieldIdsInForm.includes((field as any).getFieldId()) &&
            (field as any).getRepeatingIndex() === (form as any).getRepeatingIndex()
        )

        const field_responses: FieldResponse[] = []
        for (const ref of allFieldRefs) {
          let value = null
          const isVisible: boolean = (ref as any).getIsVisible()
          if (applyValidation) {
            value = (ref as any).getValue()

            if (value === true) {
              value = 't'
            } else if (value === false) {
              value = 'f'
            }

            const fieldInstanceId: string = (ref as any).getFieldId()
            const repeatingIndex: number = (ref as any).getRepeatingIndex()
            const isVisible: boolean = (ref as any).getIsVisible()
            const fieldType: FieldType = (ref as any).getFieldType()
            const isRequired = (ref as any)
              .getValidations()
              .some((validation: Validation) => validation.validation_type === 'responseRequired')
            if (
              isVisible &&
              isRequired &&
              (fieldType === 'FIELD_IMAGE_UPLOAD' || fieldType === 'FIELD_IMAGE_UPLOAD_MULTIPLE')
            ) {
              const { getRecapImagesForRecapField, queryAllRecapImages } = RecapImagesModel()
              addBreadcrumb({
                level: 'debug',
                category: 'RecapImageDBData',
                message: 'Current Recap Image Database Rows',
                data: {
                  rows: await queryAllRecapImages(),
                },
              })
              const images = await getRecapImagesForRecapField(
                recapId.value,
                taskOrStoreId.value,
                fieldInstanceId,
                repeatingIndex
              )
              if (!images || !images.length) {
                logInfo(
                  'error',
                  'ImageMissingRecapSave',
                  'Error: Field requiring image upload is missing images',
                  {
                    recapId: recapId.value,
                    taskId: taskOrStoreId.value,
                    fieldInstanceId,
                    repeatingIndex,
                    isVisible,
                    isRequired,
                    value,
                  }
                )
              }
            }
          } else {
            value = (ref as any).getValueRaw()
          }

          field_responses.push({
            id: null,
            value,
            events_id: -1, // Flags the response as being for a task or store
            events_custom_id: (ref as any).getEventsCustomId(),
            events_custom_instances_id: (ref as any).getFieldId(),
            events_custom_response_id: null,
            events_custom_form_response_id: null,
            visible_on_submit: isVisible,
          })
        }

        form_responses.push({
          id: null,
          events_id: -1, // Flags the response as being for a task or store
          events_custom_forms_id: (form as any).getFormId(),
          events_custom_responses_id: null,
          products_id: (form as any).getProductsId(),
          field_responses,
        })
      }

      const store = useStore()
      const recapResponse = {
        id: null,
        events_id: -1, // Flags the response as being for a task
        events_recaps_id: recapId.value,
        task_id: recapType.value === 'task' ? taskOrStoreId.value : undefined,
        store_id: recapType.value === 'store' ? taskOrStoreId.value : undefined,
        venue_id: recapType.value === 'store' ? taskOrStoreId.value : undefined,
        staffing_users_id: store.user.id,
        users_id: null,
        status_id: '1',
        version_number: recapDefinition.value?.version,
        staff_app_version: process.env.ANDROID_VERSION_NAME ?? null,
        staff_app_version_full: process.env.ANDROID_VERSION_NAME ?? null,
        last_staff_app_data_sync_uuid: null,
        staffing_users_full_name: null,
        submitter_full_name: null,
        form_responses,
      }
      return recapResponse
    } else {
      errorSubmittingRecap.value = true
      if (applyValidation && errorsArray.length > 0) {
        addBreadcrumb({
          category: 'RecapValidationErrors',
          level: 'debug',
          data: {
            errors: errorsArray,
          },
          message: 'Recap submission failed due to validation error(s)',
        })
        const container = document.querySelector('main')
        const { getScrollTarget, setVerticalScrollPosition } = scroll
        const target = getScrollTarget(container!)
        const offset = container!.offsetTop - 200
        setVerticalScrollPosition(target, offset, 500)
        return { validationError: true }
      }
    }
    return null
  }

  async function mapData(fieldId: string, repeatingIndex: number | undefined) {
    // Update any formula fields as necessary
    fields.value
      .filter((ref) => ((ref as any).getFieldType() as FieldType) === 'FIELD_FORMULA')
      .forEach((ref) => {
        const formula: Formula = (ref as any).getFormula() as Formula
        let formulaDependenceIds: string[] = []
        getFieldIdsInFormula(formula, formulaDependenceIds)
        // Remove duplicates
        formulaDependenceIds = formulaDependenceIds.filter(
          (id, index, arr) => arr.indexOf(id) === index
        )

        // Now get a map of the formula dependence field IDs to their refs
        const formulaDependenceFields: { [key: string]: (typeof RecapField)[] } = {}
        fields.value
          .filter((ref) =>
            formulaDependenceIds.includes((ref as any).getEventsCustomId() as string)
          )
          .forEach((ref) => {
            // We have arrays here in the case of repeated fields
            // e.g. Product Forms
            let arr = formulaDependenceFields[(ref as any).getEventsCustomId() as string]
            if (!arr) {
              arr = [ref]
              formulaDependenceFields[(ref as any).getEventsCustomId() as string] = arr
            } else {
              arr.push(ref)
            }
          })
        const parsedFormula = parseFormula(formula, formulaDependenceFields)
        const evaluatedFormula = eval(parsedFormula)
        if (Number.isNaN(evaluatedFormula)) {
          // eslint-disable-next-line
          ;(ref as any).getFormulaValue().value = 0
        } else {
          // eslint-disable-next-line
          ;(ref as any).getFormulaValue().value = evaluatedFormula
        }
      })

    // Update Logic Conditions
    // Check if any fields depend on this field
    const depKeys = Object.keys(fieldLogicDependencies.value)
    const logicFieldIds: string[] = depKeys.filter((key) =>
      fieldLogicDependencies.value[key].includes(fieldId)
    )

    if (logicFieldIds && logicFieldIds.length) {
      // At least one field depends on this one for logic. Find all of its dependencies as well
      const allFieldIds = Array.from(
        new Set(logicFieldIds.map((fieldId) => fieldLogicDependencies.value[fieldId]).flat())
      )

      // Get the refs for all the fields
      // `logicRefs` are each of the fields we're checking logic for
      // We only want to check logic for fields in the same repeating form, hence the repeating index check
      const logicRefs: (typeof RecapField)[] = fields.value.filter(
        (ref) =>
          logicFieldIds.includes((ref as any).getFieldId()) &&
          (ref as any).getRepeatingIndex() === repeatingIndex
      )
      // `dependenceRefs` are all the fields the current field depends on
      // We want to check all of the dependent fields so we don't
      // accidentally show a field that should be hidden due to some *other* logic condition
      const dependenceRefs: (typeof RecapField)[] = fields.value.filter((ref) =>
        allFieldIds.includes((ref as any).getFieldId() as string)
      )

      // Run all logic for the fields
      logicRefs.forEach((ref) => (ref as any).runLogic(ref, dependenceRefs, repeatingIndex))
    }

    // Form Logic Conditions
    // Check if any fields depend on this field
    const formDepKeys = Object.keys(formLogicDependencies.value)
    const formLogicFieldIds: string[] = formDepKeys.filter((key) =>
      formLogicDependencies.value[key].includes(fieldId)
    )

    if (formLogicFieldIds && formLogicFieldIds.length) {
      // At least one field depends on this one for logic. Find all of its dependencies as well
      const allFieldIds = Array.from(
        new Set(formLogicFieldIds.map((formId) => formLogicDependencies.value[formId]).flat())
      )

      // Get the refs for all the fields similar to as above
      const dependenceRefs: (typeof RecapField)[] = fields.value.filter((ref) =>
        allFieldIds.includes((ref as any).getFieldId() as string)
      )

      // Unlike above, `logicRefs` are each of the *forms* we're checking logic for
      const logicRefs: (typeof RecapForm)[] = forms.value.filter(
        (ref: typeof RecapForm) =>
          formLogicFieldIds.includes((ref as any).getFormId() as string) &&
          (ref as any).getRepeatingIndex() === repeatingIndex
      )

      // Run all logic for the forms
      logicRefs.forEach((ref) => (ref as any).runLogic(ref, dependenceRefs))
    }

    // Save the partial recap response if this is a task recap
    if (recapType.value === 'task') {
      setTimeout(async () => {
        try {
          // Only perform the partial save if we're not currently doing the final recap save before submission (e.g. task end)
          const { isSavingRecap, writeRecapResponses } = RecapResponsesModel()
          if (!isSavingRecap.value) {
            const response = await getRecapResponse(false)
            if (response) {
              await writeRecapResponses([response as RecapResponse]) // Cannot have validation errors due to the flag, guaranteed RecapResponse object
            }
          }
        } catch (error) {
          logInfo('error', 'RecapPartialSaveError', 'Error saving partial recap response', {
            recapId: recapId.value,
            fieldId,
            repeatingIndex,
            error,
          })
          Notify.create({
            type: 'warning',
            timeout: 7000,
            position: 'bottom-left',
            message: t('recap.partialSaveError'),
          })
        }
      })
    }
  }

  async function getInitialValue(fieldId: string, fieldType: FieldType, defaultValue: any) {
    let existingResponse: RecapResponse | null = null
    if (recapType.value === 'store') {
      const { getStoreRecapResponseForStoreAndRecap } = StoreRecapResponsesModel()
      existingResponse = await getStoreRecapResponseForStoreAndRecap(
        taskOrStoreId.value,
        recapId.value
      )
    } else {
      // Task recap
      const { getResponseForTask } = RecapResponsesModel()
      existingResponse = await getResponseForTask(taskOrStoreId.value)
    }

    // Fetch the existing response if there is one, and if there is find the same question and get its value
    let existingRecapResponseValue: any = null
    if (existingResponse) {
      existingResponse.form_responses.forEach((form_response) => {
        form_response.field_responses.forEach((field_response) => {
          if (String(field_response.events_custom_instances_id) === String(fieldId)) {
            existingRecapResponseValue = field_response.value
          }
        })
      })
    }

    let initialValue: any[] | boolean | string | number | null = null
    let initialSecondValue: string | null = null
    let imageCount = 0
    switch (fieldType) {
      case 'FIELD_MULTISELECT':
        if (!isNullishString(existingRecapResponseValue)) {
          initialValue = [...String(existingRecapResponseValue).split(',')]
        } else if (defaultValue) {
          initialValue = [...unref(defaultValue.split(','))]
        } else {
          initialValue = []
        }
        break
      case 'FIELD_AGREEMENT_CHECKBOX':
      case 'FIELD_YES_NO':
        if (!isNullishString(existingRecapResponseValue)) {
          if (parseBool(existingRecapResponseValue)) {
            initialValue = true
          } else {
            initialValue = false
          }
        } else {
          initialValue = parseBool(defaultValue)
        }
        break
      case 'FIELD_DATE':
        if (!isNullishString(existingRecapResponseValue)) {
          const date = new Date(existingRecapResponseValue)
          initialValue = `${date.getFullYear()}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`
        } else if (defaultValue) {
          const date = new Date(defaultValue)
          initialValue = `${date.getFullYear()}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`
        } else {
          initialValue = null
        }
        break
      case 'FIELD_TIME':
        if (!isNullishString(existingRecapResponseValue)) {
          const date = new Date(existingRecapResponseValue)
          initialValue = `${date.getHours().toString().padStart(2, '0')}:${date
            .getMinutes()
            .toString()
            .padStart(2, '0')}`
        } else if (defaultValue) {
          const date = new Date(defaultValue)
          initialValue = `${date.getHours().toString().padStart(2, '0')}:${date
            .getMinutes()
            .toString()
            .padStart(2, '0')}`
        } else {
          initialValue = null
        }
        break
      case 'FIELD_DATETIME':
        if (!isNullishString(existingRecapResponseValue)) {
          const date = new Date(existingRecapResponseValue)
          initialValue = `${date.getFullYear()}-${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date
            .getHours()
            .toString()
            .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
        } else if (defaultValue) {
          const date = new Date(defaultValue)
          initialValue = `${date.getFullYear()}-${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date
            .getHours()
            .toString()
            .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
        } else {
          initialValue = null
        }
        break
      case 'FIELD_DURATION':
        if (!isNullishString(existingRecapResponseValue)) {
          const hours = Math.floor(+existingRecapResponseValue / 60)
          const minutes = +existingRecapResponseValue % 60
          initialValue = `${hours}`
          initialSecondValue = `${minutes}`
        } else if (defaultValue) {
          const hours = Math.floor(+defaultValue / 60)
          const minutes = +defaultValue % 60
          initialValue = `${hours}`
          initialSecondValue = `${minutes}`
        } else {
          initialValue = null
        }
        break
      case 'FIELD_IMAGE_UPLOAD':
        if (!isNullishString(existingRecapResponseValue)) {
          const splitStr = existingRecapResponseValue.split(' ')
          imageCount = parseInt(splitStr[0]) || 0
          initialValue = existingRecapResponseValue
        } else {
          initialValue = null
        }
        break
      default:
        if (!isNullishString(existingRecapResponseValue)) {
          initialValue = existingRecapResponseValue
        } else if (defaultValue) {
          initialValue = unref(defaultValue)
        } else {
          initialValue = null
        }
        break
    }
    return {
      initialValue,
      initialSecondValue,
      imageCount,
    }
  }

  // Hooks
  onScopeDispose(() => {
    resetRecap()
  })
  watch(submittingPage, () => {
    // Validation Watcher
    // Every time we submit a new page, run validation logic for the page
    if (submittingPage.value) {
      // Flag the page submission as false
      submittingPage.value = false

      // Figure out which forms we're validating
      const validationFormIds = pageToFormMappings.value[selectedPage.value?.value as string]
      const validationForms = forms.value.filter((form) =>
        (validationFormIds || []).includes((form as any).getFormId())
      )

      // For each of the forms, get all the fields contained within and validate those
      validationForms.forEach((form) => {
        const fieldIdsInForm = formToFieldMappings.value[(form as any).getFormId()].map((r) => r.id)
        fields.value
          .filter((field) => fieldIdsInForm.includes((field as any).getFieldId()))
          .forEach((ref) => {
            const dependenceFieldIds: string[] =
              fieldLogicDependencies.value[(ref as any).getFieldId()]
            // `dependenceRefs` are all the fields the current field might depend on
            const dependenceRefs: (typeof RecapField)[] = fields.value.filter((field) =>
              dependenceFieldIds.includes((field as any).getFieldId() as string)
            )
            // Run validation against each field
            ;(ref as any).runValidation(ref, dependenceRefs)
          })
      })
    }
  })

  // Helper Functions
  function resetRecap(): void {
    recapId.value = ''
    recapType.value = 'task'
    taskOrStoreId.value = ''
    pages.value.splice(0, pages.value.length)
    forms.value.splice(0, forms.value.length)
    fields.value.splice(0, fields.value.length)
    formLogicDependencies.value = {}
    fieldLogicDependencies.value = {}
    pageToFormMappings.value = {}
    formToFieldMappings.value = {}
    recapTaskData.value = {}
  }
  function getPageRef(el: typeof RecapPage) {
    if (el) {
      pages.value.push(el)
    }
  }
  function getFormRef(el: typeof RecapForm) {
    if (el) {
      forms.value.push(el)
    }
  }
  function getFieldRef(el: typeof RecapField) {
    if (
      el &&
      !fieldInstanceList.value.find(
        (item) =>
          String(item.instance_id) === String((el as any).getFieldId()) &&
          String(item.repeating_index) === String((el as any).getRepeatingIndex())
      )
    ) {
      fields.value.push(el)
    }
  }

  function getPageById(id: string): Page | Nullish {
    const intId = parseInt(id)
    let page: Page | Nullish = null
    if (!isNaN(intId) && recap.value) {
      page = recap.value.recapDefinition!.pages.find((page) => page.id === id)
    }
    return page
  }
  function getFormById(id: string): Form | Nullish {
    const intId = parseInt(id)
    let form: Form | Nullish = null
    if (!isNaN(intId) && recap.value) {
      const pages = recap.value.recapDefinition!.pages
      for (const page of pages) {
        form = page.items.find((form) => parseInt(form.id) === intId)
        if (form) {
          break
        }
      }
    }
    return form
  }
  function getFieldById(id: string): Field | Nullish {
    const intId = parseInt(id)
    if (!isNaN(intId) && recap.value) {
      for (const page of recap.value.recapDefinition!.pages) {
        for (const form of page.items) {
          for (const fieldOrFieldBlock of form.items) {
            if (isFieldBlock(fieldOrFieldBlock)) {
              // Handle if we found a field block by accident
              const field = fieldOrFieldBlock.items.find((field) => parseInt(field.id) === intId)
              if (field) {
                return field
              }
            } else {
              if (parseInt(fieldOrFieldBlock.id) === intId) {
                return fieldOrFieldBlock
              }
            }
          }
        }
      }
    }
    return null
  }

  return {
    recapId,
    recapType,
    taskOrStoreId,
    recapTaskData,
    canSubmitRecap,
    setUpComposable,
    getPageRef,
    getFormRef,
    getFieldRef,
    getPageById,
    getFormById,
    getFieldById,
    getRecapResponse,
    resetRecap,
    recapDefinition,
    pages,
    forms,
    fields,
    pageToFormMappings,
    formToFieldMappings,
    formLogicDependencies,
    fieldLogicDependencies,
    getAreAllFormsForPageHidden,
    hiddenPages,
    getAreAllFieldsForFormHidden,
    recapStore,
    recapStoreToRefs,
    recapLanguage,
    submittingPage,
    allPages,
    pageOptions,
    selectedPage,
    selectPage,
    submittingRecap,
    triggerAllConditions,
    mapData,
    getInitialValue,
    errorSubmittingRecap,
  }
}

function getLogicDependenciesFromField(field: Field) {
  // Populate logical dependencies
  // This is a map of field IDs to the IDs of any fields they depend on
  let logicDeps: string[] = []
  field.logic_groups.forEach((group) => {
    const conditions = group.conditions
    const modifiers = group.modifiers
    logicDeps = logicDeps.concat(
      conditions ? conditions.map((cond) => cond.references_events_custom_instances_id) : [],
      modifiers ? modifiers.map((mod) => mod.references_events_custom_instances_id) : []
    )
  })
  logicDeps = logicDeps.concat(
    field.validations
      .filter((val) => !!val.references_events_custom_instances_id)
      .map((val) => val.references_events_custom_instances_id as string)
    // Cast to string because otherwise the type will be `(string | null)[]`
    // For some reason I couldn't get a type guard to work here
  )

  // Eliminate duplicates
  const logicDepsSet = new Set(logicDeps)
  return [...logicDepsSet]
}

function getLogicDependenciesFromForm(form: Form) {
  // Populate logical dependencies
  // This is a map of field IDs to the IDs of any fields they depend on
  let logicDeps: string[] = []
  form.logic_groups.forEach((group) => {
    const conditions = group.conditions
    logicDeps = logicDeps.concat(
      conditions ? conditions.map((cond) => cond.references_events_custom_instances_id) : []
    )
  })

  // Eliminate duplicates
  const logicDepsSet = new Set(logicDeps)
  return [...logicDepsSet]
}

function getFieldIdsInFormula(formula: Formula | FormulaOperand, fieldIds: string[]) {
  if ('operator' in formula) {
    getFieldIdsInFormula(formula.operand1, fieldIds)
    getFieldIdsInFormula(formula.operand2, fieldIds)
  } else if ('value' in formula) {
    if (formula.value && formula.value.type === 'item') {
      fieldIds.push(formula.value.item.value.split('_')[1])
    }
  }
}

function parseFormula(
  formula: Formula | FormulaOperand,
  formulaDependenceFields: { [key: string]: (typeof RecapField)[] }
) {
  let parsed = ''
  if ('operator' in formula) {
    // We need to parse the formula recursively
    parsed = [
      '(',
      parseFormula(formula.operand1, formulaDependenceFields),
      formula.operator,
      parseFormula(formula.operand2, formulaDependenceFields),
      ')',
    ].join('')
  } else if ('value' in formula) {
    // We need to insert a value into the formula
    // Default the value to 0
    let value = 0
    switch (formula.value.type) {
      case 'unit':
        // We have the actual value
        parsed = `${formula.value.unit}`
        break
      case 'item':
        // We need to retrieve the value from another field
        const fields = formulaDependenceFields[formula.value.item.value.split('_')[1]] || []
        const values = fields.map((ref) =>
          parseFloat((ref as any).getFormulaValue().value as string)
        )
        if (!values.length) {
          // For whatever reason we couldn't find any values for the specified field
          // This may happen if the field is missing from the recap
          // For now, act as if its value is 0 and move on
          values.push(0)
        }

        // Aggregate the values appropriately
        const aggregate = formula.value.item.aggregate
        if (aggregate === 'sum') {
          value = values.reduce((total, curr) => total + curr, 0)
        } else if (aggregate === 'max') {
          value = values.reduce(
            (max: number, curr: number) => (max === null ? curr : curr > max ? curr : max),
            Number.MIN_SAFE_INTEGER
          )
          if (value === Number.MIN_SAFE_INTEGER) {
            value = 0
          }
        } else if (aggregate === 'min') {
          value = values.reduce(
            (min: number, curr: number) => (min === null ? curr : curr < min ? curr : min),
            Number.MAX_SAFE_INTEGER
          )
          if (value === Number.MIN_SAFE_INTEGER) {
            value = 0
          }
        } else {
          const sum = values.reduce((total, curr) => total + curr, 0)
          value = sum / values.length
        }
        parsed = `${value}`
        break
    }
  }
  return parsed
}

export type PageOption = { label: string; value: string }
